"use client";

import { c as _c } from "react/compiler-runtime";
import { IconMenu2, IconX } from "@tabler/icons-react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "motion/react";
import Image from "next/image";
import React, { useRef, useState } from "react";
import { cn } from "@/lib/utils";
interface NavbarProps {
  children: React.ReactNode;
  className?: string;
}
interface NavBodyProps {
  children: React.ReactNode;
  className?: string;
  visible?: boolean;
}
interface NavItemsProps {
  items: {
    name: string;
    link: string;
  }[];
  className?: string;
  onItemClick?: () => void;
}
interface MobileNavProps {
  children: React.ReactNode;
  className?: string;
  visible?: boolean;
}
interface MobileNavHeaderProps {
  children: React.ReactNode;
  className?: string;
}
interface MobileNavMenuProps {
  children: React.ReactNode;
  className?: string;
  isOpen: boolean;
  onClose: () => void;
}
export const Navbar = t0 => {
  const $ = _c(12);
  const {
    children,
    className
  } = t0;
  const ref = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      target: ref,
      offset: ["start start", "end start"]
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    scrollY
  } = useScroll(t1);
  const [visible, setVisible] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = latest => {
      if (latest > 100) {
        setVisible(true);
      } else {
        setVisible(false);
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  useMotionValueEvent(scrollY, "change", t2);
  let t3;
  if ($[2] !== className) {
    t3 = cn("sticky inset-x-0 top-4 z-40 w-full", className);
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== children || $[5] !== visible) {
    let t5;
    if ($[7] !== visible) {
      t5 = child => React.isValidElement(child) ? React.cloneElement(child as React.ReactElement<{
        visible?: boolean;
      }>, {
        visible
      }) : child;
      $[7] = visible;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t4 = React.Children.map(children, t5);
    $[4] = children;
    $[5] = visible;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <motion.div ref={ref} className={t3}>{t4}</motion.div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
export const NavBody = t0 => {
  const $ = _c(14);
  const {
    children,
    className,
    visible
  } = t0;
  const t1 = visible ? "blur(10px)" : "none";
  const t2 = visible ? "0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset" : "none";
  const t3 = visible ? "40%" : "100%";
  const t4 = visible ? 20 : 0;
  let t5;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== t4) {
    t5 = {
      backdropFilter: t1,
      boxShadow: t2,
      width: t3,
      y: t4
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      type: "spring",
      stiffness: 200,
      damping: 50
    };
    t7 = {
      minWidth: "800px"
    };
    $[5] = t6;
    $[6] = t7;
  } else {
    t6 = $[5];
    t7 = $[6];
  }
  const t8 = visible && "bg-white/80 dark:bg-stone-900/80";
  let t9;
  if ($[7] !== className || $[8] !== t8) {
    t9 = cn("relative z-60 mx-auto hidden w-full max-w-7xl flex-row items-center justify-between self-start rounded-full bg-transparent px-4 py-2 lg:flex dark:bg-transparent", t8, className);
    $[7] = className;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== children || $[11] !== t5 || $[12] !== t9) {
    t10 = <motion.div animate={t5} transition={t6} style={t7} className={t9}>{children}</motion.div>;
    $[10] = children;
    $[11] = t5;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  return t10;
};
export const NavItems = t0 => {
  const $ = _c(13);
  const {
    items,
    className,
    onItemClick
  } = t0;
  const [hovered, setHovered] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setHovered(null);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = cn("absolute inset-0 hidden flex-1 flex-row items-center justify-center space-x-2 text-sm font-medium text-zinc-600 transition duration-200 hover:text-zinc-800 lg:flex lg:space-x-2", className);
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== hovered || $[4] !== items || $[5] !== onItemClick) {
    let t4;
    if ($[7] !== hovered || $[8] !== onItemClick) {
      t4 = (item, idx) => <a onMouseEnter={() => setHovered(idx)} onClick={onItemClick} className="relative px-4 py-2 text-neutral-600 dark:text-neutral-300" key={item.link} href={item.link}>{hovered === idx && <motion.div layoutId="hovered" className="absolute inset-0 h-full w-full rounded-full bg-gray-100 dark:bg-neutral-800" />}<span className="relative z-20">{item.name}</span></a>;
      $[7] = hovered;
      $[8] = onItemClick;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    t3 = items.map(t4);
    $[3] = hovered;
    $[4] = items;
    $[5] = onItemClick;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[10] !== t2 || $[11] !== t3) {
    t4 = <motion.div onMouseLeave={t1} className={t2}>{t3}</motion.div>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
};
export const MobileNav = t0 => {
  const $ = _c(16);
  const {
    children,
    className,
    visible
  } = t0;
  const t1 = visible ? "blur(10px)" : "none";
  const t2 = visible ? "0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset" : "none";
  const t3 = visible ? "90%" : "100%";
  const t4 = visible ? "12px" : "0px";
  const t5 = visible ? "12px" : "0px";
  const t6 = visible ? "4px" : "2rem";
  const t7 = visible ? 20 : 0;
  let t8;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== t4 || $[4] !== t5 || $[5] !== t6 || $[6] !== t7) {
    t8 = {
      backdropFilter: t1,
      boxShadow: t2,
      width: t3,
      paddingRight: t4,
      paddingLeft: t5,
      borderRadius: t6,
      y: t7
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      type: "spring",
      stiffness: 200,
      damping: 50
    };
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const t10 = visible && "bg-white/80 dark:bg-stone-900/80";
  let t11;
  if ($[9] !== className || $[10] !== t10) {
    t11 = cn("relative z-50 mx-auto flex w-full max-w-[calc(100vw-2rem)] flex-col items-center justify-between bg-transparent px-0 py-2 lg:hidden", t10, className);
    $[9] = className;
    $[10] = t10;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== children || $[13] !== t11 || $[14] !== t8) {
    t12 = <motion.div animate={t8} transition={t9} className={t11}>{children}</motion.div>;
    $[12] = children;
    $[13] = t11;
    $[14] = t8;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  return t12;
};
export const MobileNavHeader = t0 => {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex w-full flex-row items-center justify-between", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const MobileNavMenu = t0 => {
  const $ = _c(6);
  const {
    children,
    className,
    isOpen
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== className || $[2] !== isOpen) {
    t1 = isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className={cn("absolute inset-x-0 top-16 z-50 flex w-full flex-col items-start justify-start gap-4 rounded-lg bg-white backdrop-blur-md px-4 py-8 shadow-[0_0_24px_rgba(34,42,53,0.06),0_1px_1px_rgba(0,0,0,0.05),0_0_0_1px_rgba(34,42,53,0.04),0_0_4px_rgba(34,42,53,0.08),0_16px_68px_rgba(47,48,55,0.05),0_1px_0_rgba(255,255,255,0.1)_inset] dark:bg-stone-900 dark:backdrop-blur-md", className)}>{children}</motion.div>;
    $[0] = children;
    $[1] = className;
    $[2] = isOpen;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <AnimatePresence>{t1}</AnimatePresence>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export const MobileNavToggle = t0 => {
  const $ = _c(3);
  const {
    isOpen,
    onClick
  } = t0;
  let t1;
  if ($[0] !== isOpen || $[1] !== onClick) {
    t1 = isOpen ? <IconX className="text-black dark:text-white" onClick={onClick} /> : <IconMenu2 className="text-black dark:text-white" onClick={onClick} />;
    $[0] = isOpen;
    $[1] = onClick;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
export const NavbarLogo = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <a href="/" className="relative z-20 mr-4 flex items-center justify-center space-x-2 px-2 py-1 text-sm font-normal text-black dark:text-foreground"><Image src="/arpano-logo.svg" alt="logo" width={30} height={30} className="dark:brightness-0 dark:invert" /><span className="font-sans text-2xl text-black dark:text-foreground">ARPANO</span></a>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const NavbarButton = t0 => {
  const $ = _c(20);
  let children;
  let className;
  let href;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      href,
      as: t1,
      children,
      className,
      variant: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = href;
    $[4] = props;
    $[5] = t1;
    $[6] = t2;
  } else {
    children = $[1];
    className = $[2];
    href = $[3];
    props = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const Tag = t1 === undefined ? "a" : t1;
  const variant = t2 === undefined ? "primary" : t2;
  let T0;
  let t3;
  let t4;
  if ($[7] !== Tag || $[8] !== className || $[9] !== href || $[10] !== variant) {
    const variantStyles = {
      primary: "shadow-[0_0_24px_rgba(34,_42,_53,_0.06),_0_1px_1px_rgba(0,_0,_0,_0.05),_0_0_0_1px_rgba(34,_42,_53,_0.04),_0_0_4px_rgba(34,_42,_53,_0.08),_0_16px_68px_rgba(47,_48,_55,_0.05),_0_1px_0_rgba(255,_255,_255,_0.1)_inset] bg-primary dark:bg-primary text-white dark:text-white",
      secondary: "bg-transparent shadow-none dark:text-white",
      dark: "bg-black text-white shadow-[0_0_24px_rgba(34,_42,_53,_0.06),_0_1px_1px_rgba(0,_0,_0,_0.05),_0_0_0_1px_rgba(34,_42,_53,_0.04),_0_0_4px_rgba(34,_42,_53,_0.08),_0_16px_68px_rgba(47,_48,_55,_0.05),_0_1px_0_rgba(255,_255,_255,_0.1)_inset]",
      gradient: "bg-gradient-to-b from-blue-500 to-blue-700 text-white shadow-[0px_2px_0px_0px_rgba(255,255,255,0.3)_inset]"
    };
    T0 = Tag;
    t3 = href || undefined;
    t4 = cn("px-4 py-2 rounded-full bg-white button bg-white text-black text-sm font-bold relative cursor-pointer hover:-translate-y-0.5 transition duration-200 inline-block text-center", variantStyles[variant], className);
    $[7] = Tag;
    $[8] = className;
    $[9] = href;
    $[10] = variant;
    $[11] = T0;
    $[12] = t3;
    $[13] = t4;
  } else {
    T0 = $[11];
    t3 = $[12];
    t4 = $[13];
  }
  let t5;
  if ($[14] !== T0 || $[15] !== children || $[16] !== props || $[17] !== t3 || $[18] !== t4) {
    t5 = <T0 href={t3} className={t4} {...props}>{children}</T0>;
    $[14] = T0;
    $[15] = children;
    $[16] = props;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
};